import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { PageHeader, Card, Button, Input, Label, EmptyState } from "../components/ui-kit";
import { SearchSelect } from "../components/SearchSelect";
import { Plus, Trash2, Pencil, Upload, Download, Camera, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

export default function FarmersPage() {
  const { user } = useAuth();
  const canDelete = ["admin", "manager", "director"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    const [f, c] = await Promise.all([
      api.get("/farmers"),
      api.get("/customers").catch(() => ({ data: [] })),
    ]);
    setRows(f.data);
    setCustomers(c.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (form, id) => {
    try {
      if (id) await api.put(`/farmers/${id}`, form);
      else await api.post("/farmers", form);
      toast.success("Saved");
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete farmer?")) return;
    try {
      await api.delete(`/farmers/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("download failed:", err);
      toast.error("Download failed");
    }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/farmers/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded — ${data.created} created, ${data.updated} updated`);
      if (data.errors?.length) logger.warn("Upload row errors:", data.errors);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const filtered = rows.filter((r) =>
    q ? [r.name, r.code, r.mobile, r.village].some((v) => (v || "").toLowerCase().includes(q.toLowerCase())) : true
  );

  return (
    <div>
      <PageHeader
        title="Farmers"
        subtitle="Master database of farmers — used in Daily Report visits"
        actions={
          <>
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileRef}
              onChange={uploadFile}
              className="hidden"
              data-testid="farmers-upload-input"
            />
            <Button variant="outline" onClick={() => download("/farmers/template.xlsx", "farmers_template.xlsx")} data-testid="farmers-template-btn">
              <Download className="w-4 h-4" /> Template
            </Button>
            <Button variant="outline" onClick={() => download("/farmers/export.xlsx", "farmers_export.xlsx")} data-testid="farmers-export-btn">
              <Download className="w-4 h-4" /> Export
            </Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="farmers-upload-btn">
              <Upload className="w-4 h-4" /> Bulk upload
            </Button>
            <Button
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
              data-testid="new-farmer-btn"
            >
              <Plus className="w-4 h-4" /> New farmer
            </Button>
          </>
        }
      />

      {open && (
        <FarmerForm
          initial={editing}
          customers={customers}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
          onSave={save}
        />
      )}

      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b border-[#E5E2DC]">
          <Input
            placeholder="Search by name, mobile or village…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-sm"
            data-testid="farmers-search"
          />
        </div>
        {filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No farmers yet" description="Add a farmer or bulk-upload the Excel template." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Photo</th>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Village</th>
                  <th className="px-4 py-3">Land area</th>
                  <th className="px-4 py-3">Crops</th>
                  <th className="px-4 py-3">Linked dealer</th>
                  <th className="px-4 py-3">Notes</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60" data-testid={`farmer-row-${r.id}`}>
                    <td className="px-2 py-2">
                      <FarmerAvatar path={r.photo_path} name={r.name} size={40} testid={`farmer-avatar-${r.id}`} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs" data-testid={`farmer-code-${r.id}`}>{r.code || "—"}</td>
                    <td className="px-4 py-3 font-semibold">{r.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.mobile || "—"}</td>
                    <td className="px-4 py-3">{r.village || "—"}</td>
                    <td className="px-4 py-3" data-testid={`farmer-land-${r.id}`}>{r.land_area || "—"}</td>
                    <td className="px-4 py-3 text-[#5C6B5E] max-w-[220px]" data-testid={`farmer-crops-${r.id}`}>
                      {Array.isArray(r.crops) && r.crops.length ? (
                        <div className="flex flex-wrap gap-1">
                          {r.crops.map((c, i) => (
                            <span key={`${r.id}-crop-${i}`} className="inline-block px-1.5 py-0.5 rounded bg-[#A3B18A]/25 text-[#1E3F20] text-[10px] font-semibold uppercase tracking-wider">
                              {c}
                            </span>
                          ))}
                        </div>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3" data-testid={`farmer-linked-${r.id}`}>
                      {r.linked_customer_id
                        ? (() => {
                            const c = customers.find((x) => x.id === r.linked_customer_id);
                            return c ? (
                              <div>
                                <div className="text-xs font-semibold">{c.name}</div>
                                <div className="text-[10px] text-[#5C6B5E] font-mono">{c.code}</div>
                              </div>
                            ) : "—";
                          })()
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-[#5C6B5E] max-w-xs truncate" title={r.notes}>{r.notes || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          className="text-xs px-2.5 py-1 rounded bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 font-semibold inline-flex items-center gap-1"
                          onClick={() => {
                            setEditing(r);
                            setOpen(true);
                          }}
                          data-testid={`edit-farmer-${r.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        {canDelete && (
                          <button className="text-xs p-1.5 rounded hover:bg-red-50 text-red-600" onClick={() => del(r.id)} data-testid={`delete-farmer-${r.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function FarmerForm({ initial, customers = [], onClose, onSave }) {
  const [form, setForm] = useState({
    name: "",
    code: "",
    mobile: "",
    village: "",
    notes: "",
    photo_path: "",
    land_area: "",
    crops: [],
    linked_customer_id: "",
    ...(initial || {}),
  });
  const [cropsInput, setCropsInput] = useState(
    Array.isArray(initial?.crops) ? initial.crops.join(", ") : ""
  );
  const [uploading, setUploading] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return toast.error("Name is required");
    const crops = cropsInput
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
    onSave({ ...form, crops }, initial?.id);
  };
  const onPhoto = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/farmers/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, photo_path: data.path });
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <Card className="mb-6" data-testid="farmer-form">
      <h3 className="font-heading text-xl font-bold mb-4">{initial ? "Edit" : "New"} farmer</h3>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2 flex items-center gap-4">
          <FarmerAvatar path={form.photo_path} name={form.name} size={72} testid="farmer-form-avatar" />
          <div>
            <label className="inline-flex items-center gap-2 cursor-pointer bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 rounded-lg px-4 py-2 font-semibold text-sm">
              <Camera className="w-4 h-4" /> {form.photo_path ? "Replace photo" : "Add photo"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="farmer-photo-input"
                onChange={(e) => onPhoto(e.target.files?.[0])}
              />
            </label>
            {uploading && <span className="text-xs text-[#5C6B5E] ml-2">Uploading…</span>}
            {form.photo_path && (
              <button
                type="button"
                onClick={() => setForm({ ...form, photo_path: "" })}
                className="text-xs text-red-600 ml-3 hover:underline"
                data-testid="farmer-photo-clear"
              >
                Remove
              </button>
            )}
          </div>
        </div>
        <div>
          <Label>Farmer code (optional — unique)</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. F-001" data-testid="farmer-code" />
        </div>
        <div>
          <Label>Name</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="farmer-name" />
        </div>
        <div>
          <Label>Mobile</Label>
          <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="farmer-mobile" />
        </div>
        <div>
          <Label>Village</Label>
          <Input value={form.village} onChange={(e) => setForm({ ...form, village: e.target.value })} data-testid="farmer-village" />
        </div>
        <div>
          <Label>Land area</Label>
          <Input
            value={form.land_area}
            onChange={(e) => setForm({ ...form, land_area: e.target.value })}
            placeholder="e.g. 2.5 acres"
            data-testid="farmer-land-area"
          />
        </div>
        <div className="sm:col-span-2">
          <Label>Crops (comma-separated)</Label>
          <Input
            value={cropsInput}
            onChange={(e) => setCropsInput(e.target.value)}
            placeholder="e.g. Wheat, Rice, Tomato"
            data-testid="farmer-crops-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label>Linked nearby dealer (optional)</Label>
          <SearchSelect
            value={form.linked_customer_id}
            onChange={(v) => setForm({ ...form, linked_customer_id: v })}
            testId="farmer-linked-customer"
            options={[
              { value: "", label: "— None —" },
              ...customers.map((c) => ({
                value: c.id,
                label: c.name,
                sublabel: [c.code, c.city].filter(Boolean).join(" · "),
                keywords: `${c.name} ${c.code || ""} ${c.city || ""}`,
              })),
            ]}
            placeholder="Search dealer by name, code, or city…"
          />
        </div>
        <div className="sm:col-span-2">
          <Label>Notes</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="farmer-notes" />
        </div>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={uploading} data-testid="farmer-submit">{initial ? "Save changes" : "Create farmer"}</Button>
        </div>
      </form>
    </Card>
  );
}

export function FarmerAvatar({ path, name, size = 40, testid }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let cancelled = false;
    let blobUrl = "";
    (async () => {
      if (!path) return;
      try {
        const res = await api.get("/farmers/photo", { params: { path }, responseType: "blob" });
        blobUrl = URL.createObjectURL(res.data);
        if (!cancelled) setSrc(blobUrl);
      } catch (err) {
        // Photo may be missing or unauthorized — silent fallback to initial avatar is intentional.
        logger.debug("farmer photo load failed:", err?.message);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path]);
  const initial = (name || "?").slice(0, 1).toUpperCase();
  if (!path) {
    return (
      <div
        data-testid={testid}
        className="rounded-full bg-[#A3B18A]/30 text-[#1E3F20] font-bold grid place-items-center"
        style={{ width: size, height: size, fontSize: Math.round(size / 2.4) }}
      >
        <User className="w-1/2 h-1/2 opacity-60" />
      </div>
    );
  }
  return src ? (
    <img
      src={src}
      alt={name || "Farmer"}
      data-testid={testid}
      className="rounded-full object-cover border border-[#E5E2DC]"
      style={{ width: size, height: size }}
    />
  ) : (
    <div
      data-testid={testid}
      className="rounded-full bg-[#E5E2DC] animate-pulse"
      style={{ width: size, height: size }}
    >{initial}</div>
  );
}
